import { ScreenSchema, ValidationResult } from '../../../@types/validation-types'
import { validationRules } from '../rules/validationRules'
import { BuildingEnergyRatingType, ContentStateInterface } from '../../../@types/types'
import { AddressFeatureState } from '../../../@types/state-types'

export const noOpValidators: ValidationResult = {
  address: {
    fields: {
      street: [],
      houseNumber: [],
      postcode: [],
      city: [],
    },
    validated: true,
  },
  basicInfo: {
    fields: {
      buildingType: [],
      mainFloorSpace: [],
      separableSpace: [],
      price: [],
      room: [],
      additionalCosts: [],
      deposit: [],
    },
    validated: true,
  },
  costs: {
    fields: {
      additionalCosts: [],
      deposit: [],
    },
    validated: true,
  },
  description: {
    fields: {
      adTitle: [],
      adDescription: [],
      adLocation: [],
      adOther: [],
    },
    validated: true,
  },
  contactInfo: {
    fields: {
      salutation: [],
      lastName: [],
      phoneNumber: [],
      email: [],
    },
    validated: true,
  },
  multimedia: {
    validated: true,
  },
  otherFacilities: {
    fields: {
      totalFloorsNumber: [],
      parkingSpaceRent: [],
      noParkingSpaces: [],
    },
    validated: true,
  },
  connections: {
    fields: {
      distanceToPT: [],
      distanceToMRS: [],
      distanceToFM: [],
      distanceToAirport: [],
    },
    validated: true,
  },
  construction: {
    fields: {
      constructionLastModernization: [],
      constructionYear: [],
      legalConstructionYear: [],
      thermalCharacteristic: [],
    },
    validated: true,
  },
  tenantDocuments: {
    fields: {
      solvency: [],
      rental: [],
      income: [],
      selfReport: [],
      identification: [],
    },
    validated: true,
  },
  flatShareRoomProperties: {
    fields: {},
    validated: true,
  },
  whoWeLookFor: {
    validated: true,
    fields: {
      requestedGender: [],
      numberOfRequestedFlatMates: [],
      ageOfRequestedFrom: [],
      ageOfRequestedTo: [],
      flatShareSize: [],
      numberOfMaleFlatMates: [],
      numberOfFemaleFlatMates: [],
      ageOfFlatMatesFrom: [],
      ageOfFlatMatesTo: [],
    },
  },
  apiSearchData: {
    validated: true,
    fields: {
      searchField1: [],
      searchField2: [],
      searchField3: [],
    },
  },
  realtorLeadEngine: {
    fields: {
      conversionToLeadAllowed: [],
    },
    validated: true,
  },
  objectNumbers: {
    fields: {
      externalId: [],
      groupNumber: [],
    },
    validated: true,
  },
  objectDetails: {
    fields: {
      floor: [],
      totalFloorsNumber: [],
      constructionCondition: [],
      heatingType: [],
      wbsRequired: [],
    },
    validated: true,
  },
}

//standard
export const defaultAddress = {
  street: validationRules.street,
  houseNumber: validationRules.houseNumber,
  postcode: validationRules.postcode,
  city: validationRules.city,
}
//defaultAddressWithInternational

export const manualAddress = {
  ...defaultAddress,
  manualCity: validationRules.manualCity,
  manualRegion: validationRules.manualRegion,
  manualQuarter: validationRules.manualQuarter,
}

export const internationalAddress = {
  ...defaultAddress,
  street: validationRules.internationalStreet,
  houseNumber: validationRules.internationalHouseNumber,
  postcode: validationRules.internationalPostcode,
  manualRegion: validationRules.manualRegion,
  manualCountry: validationRules.manualCountry,
}

export const useManualAndInternationalAddress = (
  _insertionData: ContentStateInterface,
  addressFeature: AddressFeatureState
) => {
  if (addressFeature.isInternationalAddress) {
    return internationalAddress
  }
  if (addressFeature.isManualAddress) {
    return manualAddress
  } else {
    return defaultAddress
  }
}

export const defaultDescription = {
  adTitle: validationRules.adTitle,
  adDescription: validationRules.adDescription,
  adLocation: validationRules.adLocation,
  adOther: validationRules.adOther,
}
export const defaultContactInfo = {
  salutation: validationRules.salutation,
  lastName: validationRules.name,
  firstName: validationRules.name,
  phoneNumber: validationRules.phoneNumber,
  email: validationRules.email,
}

export const defaultFlatShareRoomProperties = {}

export const defaultConnections = {
  distanceToPT: validationRules.distanceToPT,
  distanceToMRS: validationRules.distanceToMRS,
  distanceToFM: validationRules.distanceToFM,
  distanceToAirport: validationRules.distanceToAirport,
}
export const defaultConstruction = {
  constructionLastModernization: validationRules.constructionLastModernization,
  constructionYear: validationRules.constructionYear,
  thermalCharacteristic: (insertionData: ContentStateInterface, addressFeature: AddressFeatureState) => {
    if (
      addressFeature.isInternationalAddress &&
      insertionData.buildingEnergyRatingType &&
      insertionData.buildingEnergyRatingType !== BuildingEnergyRatingType.NO_INFORMATION
    ) {
      return validationRules.thermalCharacteristicRequired
    }
    return validationRules.thermalCharacteristic
  },
  legalConstructionYear: validationRules.legalConstructionYear,
}

export const defaultCosts = {
  additionalCosts: validationRules.additionalCosts,
  deposit: validationRules.deposit,
}

export const defaultApiSearchData = {
  searchField1: validationRules.apiSearchField,
  searchField2: validationRules.apiSearchField,
  searchField3: validationRules.apiSearchField,
}

export const defaultObjectNumbers: ScreenSchema = {
  externalId: validationRules.externalId,
  groupNumber: validationRules.groupNumber,
}

export const getSchemaForOtherFacilitiesByIntermediateRent = (insertionData: ContentStateInterface) => {
  if (insertionData.intermediateRent) {
    return {
      adTitle: validationRules.adTitle,
      moveInDate: validationRules.moveInDateRequiredAndStrict,
      freeUntil: validationRules.freeUntilRequiredAndStrict,
      minimumTermOfLease: validationRules.minimumTermOfLease,
      floor: validationRules.floor,
      totalFloorsNumber: validationRules.totalFloorsWithDependingFloorNumber,
    }
  } else {
    return {
      adTitle: validationRules.adTitle,
      moveInDate: validationRules.moveInDateRequired,
      freeUntil: [],
      minimumTermOfLease: validationRules.minimumTermOfLease,
      floor: validationRules.floor,
      totalFloorsNumber: validationRules.totalFloorsWithDependingFloorNumber,
    }
  }
}

export const useRentPriceTypeCondition = (insertionData: ContentStateInterface) =>
  insertionData.priceType?.value === 'REQUEST_RENT_PRICE' ? [] : validationRules.price

export const usePurchasePriceOnRequestCondition = (insertionData: ContentStateInterface) =>
  insertionData.priceType?.value === 'REQUEST_PURCHASE_PRICE' ? [] : validationRules.price

export const defaultHangarBasicInfo = {
  buildingType: validationRules.buildingType,
  totalFloorSpace: validationRules.totalFloorSpace,
  plotArea: validationRules.plotArea,
  mainFloorSpace: validationRules.mainFloorSpaceHangar,
  separableSpace: validationRules.separableSpaceHangar,
  price: useRentPriceTypeCondition,
}

export const defaultHangarOtherFacilities = {
  floorLoad: validationRules.floorLoad,
  powerSupply: validationRules.powerSupply,
  loadLiftingValue: validationRules.loadLiftingValue,
  craneValue: validationRules.craneValue,
  parkingSpaceRent: validationRules.parkingSpaceRent,
  noParkingSpaces: validationRules.noParkingSpaces,
}

export const defaultGastronomyBasicInfo = {
  buildingType: validationRules.buildingType,
  totalFloorSpace: validationRules.totalFloorSpace,
  mainFloorSpace: validationRules.mainFloorSpace,
  auxiliaryArea: validationRules.auxiliaryArea,
  price: useRentPriceTypeCondition,
}

export const defaultGastronomyOtherFacilities = {
  numberOfSeats: validationRules.numberOfSeats,
  numberOfBeds: validationRules.numberOfBeds,
  totalFloorsNumber: validationRules.totalFloors,
  noParkingSpaces: validationRules.noParkingSpaces,
  parkingSpaceRent: validationRules.parkingSpaceRent,
}

export const defaultOfficeBasicInfo = {
  buildingType: validationRules.buildingType,
  mainFloorSpace: validationRules.mainFloorSpaceRequired,
  separableSpace: validationRules.separableSpace,
  price: useRentPriceTypeCondition,
}

export const defaultOfficeRentOtherFacilities = {
  totalFloorsNumber: validationRules.totalFloors,
  parkingSpaceRent: validationRules.parkingSpaceRent,
  noParkingSpaces: validationRules.noParkingSpaces,
}
